import {SideObject} from '@popperjs/core';
import {compact} from 'lodash';
import memoize from 'lru-memoize';
import React, {ReactNode} from 'react';

import {KeyboardActionsEnum} from '../../../../libs/shared/core/src/helpers/browser/keyboardHelpers';
import {Localizer} from '../../../../libs/shared/core/src/helpers/internationalization/localizeTypes';
import {defineMessage, Trans} from '../../../../libs/shared/core/src/helpers/internationalization/react';
import {CompanyFeaturesEnum} from '../../../../libs/shared/core/src/models/api/company/companyModel';
import {UserThemesEnum} from '../../../../libs/shared/core/src/models/api/userThemesEnum';
import {AppContentTypesEnum} from '../../../../libs/shared/core/src/store/panels/app/commonAppPanelState';
import {PanelTypesEnum} from '../../../../libs/shared/core/src/store/panels/panelStateBase';
import {IconName} from '../../../../libs/shared/ui/src/components/icon/icon';
import {AppRoutesPrefixesEnum} from '../../../../libs/shared/ui/src/routing/appRoutesPrefixes';
import {expandedDarkTheme} from '../../../../libs/shared/ui/src/theme/dark/darkTheme';
import {UserTheme} from '../../../../libs/shared/ui/src/theme/themeTypes';
import {PermissionsEnum} from '../../../models/api/roleModel';

/*
 * Types.
 */

export interface AppDefinition {
  iconName: IconName;
  title: ReactNode;
  localizedTitle?: string;
  contentType: AppContentTypesEnum;
  /** @deprecated Use the `contentType` property instead. */
  path: string;
  /** @deprecated Use the `contentType` property instead. */
  contentPanelType?: PanelTypesEnum | string; // @TODO: Remove string when CONTACTS is in PanelTypesEnum.
  permission?: PermissionsEnum;
  feature?: CompanyFeaturesEnum;
  keyboardAction?: KeyboardActionsEnum;
}

export enum TrackingLocationsEnum {
  P0 = 'p0',
}

/*
 * Constants.
 */

export const topBarHeight = 52;
export const topBarHorizontalPadding = 14;
export const topBarVerticalPadding = 10;
export const topBarButtonGap = 12;

export const minTopBarSearchBarWidth = 314;
export const maxTopBarSearchBarWidth = 530;
export const topBarSearchBarFlexBasis = maxTopBarSearchBarWidth;
// - The flex basis should be the same on both sides, so that the search bar is centered (until things start shrinking).
// - Set the flex basis such that the search bar will only start shrinking when the screen is less than 1400px wide.
export const topBarSideFlexBasis = Math.floor(
  (1400 - topBarHorizontalPadding * 2 - topBarButtonGap * 2 - topBarSearchBarFlexBasis) / 2,
);

export const screenWidthLimitForUpgradeTrialButton = 1100;

export const topBarTooltipCustomMargin = 4;
export const topBarTooltipPreventOverflowPadding: Partial<SideObject> = {
  // Align the left/right edge of the tooltip with the left/right edge of the anchor.
  left: topBarHorizontalPadding - topBarTooltipCustomMargin,
  right: topBarHorizontalPadding - topBarTooltipCustomMargin,
};

/*
 * App definitions.
 */

interface BuildAppsOptions {
  shouldDisplayContactsManager: boolean;
  shouldDisplayAuditTrail: boolean;
  shouldDisplayKnowledgeBase: boolean;
  // Needed for the palette.
  localize?: Localizer;
}

export const buildApps = memoize()((options: BuildAppsOptions) =>
  compact<AppDefinition>([
    {
      iconName: 'inboxFilled',
      title: <Trans id="tab-inbox">Inbox</Trans>,
      localizedTitle:
        options.localize &&
        options.localize(
          defineMessage({
            id: 'tab-inbox',
            message: 'Inbox',
          }),
        ),
      contentType: AppContentTypesEnum.INBOXES,
      path: `/${AppRoutesPrefixesEnum.INBOXES}`,
      contentPanelType: PanelTypesEnum.INBOXES,
      keyboardAction: KeyboardActionsEnum.SWITCH_TO_INBOX,
    },
    {
      iconName: 'calendar',
      title: <Trans id="tab-calendar">Calendar</Trans>,
      localizedTitle:
        options.localize &&
        options.localize(
          defineMessage({
            id: 'tab-calendar',
            message: 'Calendar',
          }),
        ),
      contentType: AppContentTypesEnum.CALENDARS,
      path: `/${AppRoutesPrefixesEnum.CALENDAR}`,
      contentPanelType: PanelTypesEnum.CALENDARS,
      keyboardAction: KeyboardActionsEnum.SWITCH_TO_CALENDAR,
    },
    options.shouldDisplayContactsManager && {
      iconName: 'contacts',
      title: <Trans id="tab-contacts">Contacts</Trans>,
      localizedTitle:
        options.localize &&
        options.localize(
          defineMessage({
            id: 'tab-contacts',
            message: 'Contacts',
          }),
        ),
      contentType: AppContentTypesEnum.CONTACTS,
      path: `/${AppRoutesPrefixesEnum.CONTACTS}`,
      contentPanelType: 'CONTACTS', // @TODO: Replace with PanelTypesEnum.
      keyboardAction: KeyboardActionsEnum.SWITCH_TO_CONTACTS,
    },
    {
      iconName: 'analytics',
      title: <Trans id="tab-analytics">Analytics</Trans>,
      localizedTitle:
        options.localize &&
        options.localize(
          defineMessage({
            id: 'tab-analytics',
            message: 'Analytics',
          }),
        ),
      contentType: AppContentTypesEnum.ANALYTICS,
      path: `/${AppRoutesPrefixesEnum.ANALYTICS}`,
      contentPanelType: PanelTypesEnum.ANALYTICS,
      permission: PermissionsEnum.READ_ANALYTICS,
      feature: CompanyFeaturesEnum.ANALYTICS,
      keyboardAction: KeyboardActionsEnum.SWITCH_TO_ANALYTICS,
    },
    options.shouldDisplayKnowledgeBase && {
      iconName: 'knowledgeBaseFilled',
      title: <Trans id="tab-knowledge-base">Knowledge Base</Trans>,
      localizedTitle:
        options.localize &&
        options.localize(
          defineMessage({
            id: 'tab-knowledge-base',
            message: 'Knowledge Base',
          }),
        ),
      contentType: AppContentTypesEnum.KNOWLEDGE_BASE,
      path: `/${AppRoutesPrefixesEnum.KNOWLEDGE_BASE}`,
      contentPanelType: PanelTypesEnum.KNOWLEDGE_BASE,
      keyboardAction: KeyboardActionsEnum.SWITCH_TO_KNOWLEDGE_BASE,
    },
    {
      iconName: 'sequences',
      title: <Trans id="tab-sequences">Sequences</Trans>,
      localizedTitle:
        options.localize &&
        options.localize(
          defineMessage({
            id: 'tab-sequences',
            message: 'Sequences',
          }),
        ),
      contentType: AppContentTypesEnum.SEQUENCES,
      path: `/${AppRoutesPrefixesEnum.SEQUENCES}`,
      permission: PermissionsEnum.READ_SEQUENCE,
      feature: CompanyFeaturesEnum.SEQUENCES,
    },
    // New Audit trail view
    options.shouldDisplayAuditTrail && {
      iconName: 'auditTrail',
      title: <Trans id="tab-audittrail">Audit trail</Trans>,
      localizedTitle:
        options.localize &&
        options.localize(
          defineMessage({
            id: 'tab-audittrail',
            message: 'Audit trail',
          }),
        ),
      contentType: AppContentTypesEnum.AUDIT_TRAIL,
      path: `/${AppRoutesPrefixesEnum.AUDIT_TRAIL}`,
      permission: PermissionsEnum.READ_AUDIT,
    },
  ]),
);

// In the "Dark Sidebar" theme, we need to render the brand color using the dark mode variation even though
// the rest of the sidebar is rendered using the provided light theme.
export function shouldOverrideTopBarBrandColor(
  themePreference: UserThemesEnum,
  appContentType?: AppContentTypesEnum,
) {
  return (
    (appContentType === AppContentTypesEnum.INBOXES || appContentType === AppContentTypesEnum.SETTINGS) &&
    themePreference === UserThemesEnum.DARK_SIDEBAR
  );
}

export function findTopBarBrandColor(theme: UserTheme, isDarkBrandColorOverride: boolean) {
  if (isDarkBrandColorOverride) {
    return expandedDarkTheme.brand;
  }

  return theme.brand;
}
